import { useTranslation } from 'react-i18next'

import { buttonClasses } from '@/design-system/Button'
import { Badge } from '@/design-system/primitives'
import { formatDateTime } from '@/lib/datetime'
import { formatMoney } from '@/lib/money'

import type { Supplement } from './types'

const STATUS_LABELS: Record<string, string> = {
  pending_payment: 'order.pending',
  paid: 'order.paid',
  expired: 'order.expired',
  cancelled: 'order.cancelled',
  refunded: 'order.refunded',
  partially_refunded: 'order.partiallyRefunded',
}

/**
 * Des plats ajoutés à une réservation, avec **leur** règlement.
 *
 * Le numéro de commande ne change pas ; ce qui change, c'est ce qu'il reste à
 * payer — et c'est ce lot, pas la table, qui le dit. Une table réglée reste
 * réglée : la carte n'affiche que « Supplément à régler » quand il le faut,
 * avec sa propre référence et son propre lien.
 *
 * `compact` : la version courte, sous la liste des billets de « Mon Masked
 * Night » — les consignes de paiement complètes vivent sur la page de suivi.
 */
export function SupplementCard({
  supplement,
  timeZone,
  compact = false,
}: {
  supplement: Supplement
  timeZone: string
  compact?: boolean
}) {
  const { t, i18n } = useTranslation()
  const money = (minor: number) =>
    formatMoney({ amount_minor: minor, currency: supplement.currency }, i18n.language)
  const when = (iso: string) => formatDateTime(iso, i18n.language, timeZone)
  const pending = supplement.status === 'pending_payment'
  const dead = ['cancelled', 'expired', 'refunded'].includes(supplement.status)
  // Le lien en bouton : le recopier depuis un téléphone, personne ne le fait.
  const payUrl =
    pending && !supplement.pays_on_site
      ? (supplement.payment_link ?? supplement.payment?.redirect_url ?? null)
      : null

  return (
    <div
      className={
        dead
          ? 'rounded-lg border border-line-subtle px-4 py-4 opacity-70 sm:px-5'
          : 'rounded-lg border border-line-subtle px-4 py-4 sm:px-5'
      }
    >
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-2">
        <p className="font-display text-heading-sm text-content-primary">
          {t('supplement.label', { sequence: supplement.sequence })}
        </p>
        <Badge tone={supplement.status === 'paid' ? 'success' : pending ? 'ember' : 'neutral'}>
          {t(STATUS_LABELS[supplement.status] ?? 'order.pending')}
        </Badge>
      </div>
      {supplement.merged_from_number ? (
        <p className="mt-1 text-caption text-content-muted">
          {t('supplement.mergedFrom', { number: supplement.merged_from_number })}
        </p>
      ) : null}

      <ul className="mt-3 space-y-1 text-sm">
        {supplement.items.map((item, index) => (
          <li key={`${item.label}-${index}`} className="flex justify-between gap-4">
            <span className="min-w-0 break-words text-content-secondary">
              {item.quantity} × {item.label}
            </span>
            <span className="shrink-0 tabular-nums text-content-primary">
              {money(item.line_total_minor)}
            </span>
          </li>
        ))}
      </ul>

      {pending && supplement.amount_due_minor > 0 ? (
        <p className="mt-3 text-sm font-medium text-ember-300">
          {t('supplement.due', { amount: money(supplement.amount_due_minor) })}
        </p>
      ) : (
        <p className="mt-3 flex justify-between gap-4 text-sm">
          <span className="text-content-muted">{t('ticketing.total')}</span>
          <span className="tabular-nums text-content-primary">{money(supplement.total_minor)}</span>
        </p>
      )}

      {supplement.cancel_reason && dead ? (
        <p className="mt-2 text-caption text-content-muted">{supplement.cancel_reason}</p>
      ) : null}

      {pending && supplement.pays_on_site ? (
        <p className="mt-2 text-sm text-content-secondary">{t('supplement.onSite')}</p>
      ) : null}

      {pending && !supplement.pays_on_site && !compact ? (
        <div className="mt-4 space-y-3">
          {supplement.payment_reference ? (
            <div className="rounded-lg border border-ember-500/40 bg-ember-500/10 px-4 py-3">
              <p className="overline text-content-muted">{t('supplement.reference')}</p>
              <p className="mt-1 select-all break-all font-display text-heading-md tracking-wide text-ember-300">
                {supplement.payment_reference}
              </p>
              <p className="mt-1 text-xs text-content-muted">{t('payment.referenceHint')}</p>
            </div>
          ) : null}
          {payUrl ? (
            <a
              href={payUrl}
              target="_blank"
              rel="noreferrer noopener"
              className={buttonClasses({ size: 'md' })}
            >
              {t('supplement.pay', { amount: money(supplement.amount_due_minor) })}
            </a>
          ) : null}
          {supplement.cancel_after ? (
            <p className="text-caption text-state-warning">
              {t('supplement.cancelAfter', { date: when(supplement.cancel_after) })}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
